import { useContext, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useUser } from '@r/api/auth.tsx';
import { Card, CardContent, CardFooter, CardHeader } from '@ui/card.tsx';
import { RocketIcon, CoinsIcon } from 'lucide-react';
import { Progress } from '../../components/home/progress.tsx';
import WidgetHome from '@client/components/widget/widget-home.tsx';

import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';

import defaultSlide from '@r/assets/images/fiddy/default-image.png';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { WorkspaceContext } from '@r/context/workspace-context.ts';
import { WorkspaceStepsInfoProcessor } from '@r/utils/workspaceSteps/workspace-steps-info-processor.ts';
import {
  isTargetLogicWorkspace,
  isLinearLogicWorkspace,
  isStepLogicWorkspace
} from '@r/utils/workspace-utils.ts';
import { resolvePointExpirationDate } from '@r/utils/date-utils.ts';

import { ProgressTarget } from '@client/components/home/progress-target.tsx';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from '@ui/skeleton.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import Marquee from '@ui/marquee.tsx';
import CountUp from 'react-countup';
import { useBalance } from '@r/context/balance-context.ts';

import { cn } from '@r/utils/utils.ts';
import { HomeSlider } from '@client/components/home/home-slider.tsx';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import type { LogicTypeStepWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';
import { useApiWidgetWorkspacesGetCollection } from '@r/api/generated/client.ts';
import { isHomeWidgetKey } from '@r/helpers/widget-workspace-slides.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { PageContainer } from '@client/components/layout/page-container.tsx';

type WorkspaceStep = LogicTypeStepWorkspaceReadModel;

const HomePage = () => {
  const { t } = useTranslation();

  const widgetWorkspacesQuery = useApiWidgetWorkspacesGetCollection({
    query: {
      queryKey: ['widget-workspaces'],
      staleTime: 5 * 60 * 1000
    }
  });
  useOrvalQueryErrorToast(widgetWorkspacesQuery, t('toast.error.label'));

  const { data: widgetWorkspaces } = widgetWorkspacesQuery;

  const activeHomeWidgetWorkspaces = useMemo(
    () =>
      (widgetWorkspaces ?? []).filter(
        (workspace) => workspace.isActive && isHomeWidgetKey(workspace.widget?.key)
      ),
    [widgetWorkspaces]
  );

  const user = useUser().data;
  const workspaceContext = useContext(WorkspaceContext);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);

  const { workspace, isWorkspacePending, isWorkspaceError, isWorkspaceSuccess } = workspaceContext;
  const { balance } = useBalance();

  let programSlides = useGetConfigByType('media', 'program-slider', true);

  if (!programSlides) {
    programSlides = [defaultSlide];
  }

  let workspaceSteps: WorkspaceStep[] = [];
  let lastReachedStep: WorkspaceStep | null | undefined = null;
  let stepAccomplishments: number[] = [];
  let nextStep: WorkspaceStep | null = null;
  let isLastStepReached = false;

  if (!programSlides) {
    programSlides = [defaultSlide];
  }

  if (isWorkspaceSuccess && workspace && user) {
    if (isStepLogicWorkspace(workspace)) {
      const {
        workspaceSteps: getWorkspaceSteps,
        stepAccomplishments: getStepAccomplishments,
        nextStep: getNextStep,
        isLastStepReached: getIsLastStepReached,
        lastReachedStep: getLastReachedStep
      } = WorkspaceStepsInfoProcessor(workspace, user, balance);

      workspaceSteps = getWorkspaceSteps;
      lastReachedStep = getLastReachedStep;
      stepAccomplishments = getStepAccomplishments;
      nextStep = getNextStep;
      isLastStepReached = getIsLastStepReached;
    }
  }

  const currencySymbol = user?.visibleWorkspaces?.[0]?.company?.country?.currencySymbol;
  const targetStatus = user?.visibleWorkspaces?.[0]?.company?.targetStatuses?.[0] || {
    id: 0,
    value: 0,
    targetValue: 0
  };
  const targetStatuses = user?.visibleWorkspaces?.[0]?.company?.targetStatuses;
  const isTargetPointLogic =
    workspace?.currentLogicWorkspace?.logic.type === LogicLogicReadModelType.TARGET_POINT;

  // Date d'expiration des points (logique linéaire), formatée pour l'affichage.
  const resolvedPointExpiration = resolvePointExpirationDate(
    workspace?.currentLogicWorkspace?.pointExpirationDate
  );
  const pointExpirationDate = resolvedPointExpiration
    ? format(resolvedPointExpiration, 'dd/MM/yyyy', { locale: fr })
    : null;

  const isTargetStatusVisible = () => {
    if (!isTargetLogicWorkspace(workspace)) {
      return true;
    }
    return targetStatuses != null && targetStatuses.length > 0;
  };

  return (
    <PageContainer className="pt-10">
      <section id="section-program" className="pb-7">
        <h1 className="mb-10 font-bold text-2xl text-foreground md:text-3xl lg:mb-16 lg:text-[2.75rem] lg:leading-[3.5rem]">
          <Trans
            i18nKey={t('home.program.preview.title', {
              workspaceName: workspaceConfigContext?.groupedConfigs?.core?.title
            })}
            components={{ 1: <br /> }}
          />
        </h1>

        {(isWorkspacePending || isWorkspaceError) && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="grid grid-cols-1 gap-4 w-full grow sm:grid-cols-2">
              <Card className="w-full border-0">
                <CardHeader className="text-lg font-semibold">
                  <Skeleton className="w-full h-7" />
                </CardHeader>

                <CardContent>
                  <Skeleton className="w-full h-10" />
                </CardContent>

                <CardFooter className="flex items-end grow">
                  <Skeleton className="w-full h-10 rounded-full" />
                </CardFooter>
              </Card>

              <Card className="w-full border-0">
                <CardHeader className="text-lg font-semibold">
                  <Skeleton className="w-full h-7" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="w-full h-10" />
                </CardContent>
                <CardFooter className="flex items-end grow">
                  <Skeleton className="w-full h-10 rounded-full" />
                </CardFooter>
              </Card>
            </div>

            <Skeleton className="w-full min-h-[350px] rounded-3xl bg-muted-foreground/10 space-y-4 xl:min-w-6/12" />
          </div>
        )}

        {isWorkspaceSuccess && (
          <div
            className={cn(
              'grid grid-cols-1 xl:grid-cols-3 gap-4',
              isTargetStatusVisible() && 'xl:grid-cols-2',
              isLinearLogicWorkspace(workspace) && 'xl:grid-cols-3'
            )}
          >
            {isStepLogicWorkspace(workspace) && workspaceSteps && (
              <div className="grid grid-cols-1 gap-4 w-full">
                <Card className="hidden p-7 text-center border-0 xs:block">
                  <Progress
                    stepAccomplishments={stepAccomplishments}
                    steps={workspaceSteps}
                    currentPoints={balance}
                  />
                </Card>

                <div
                  className={`grid grid-cols-1 gap-4 ${isLastStepReached ? '' : 'sm:grid-cols-2'}`}
                >
                  <Card className="flex flex-col justify-center w-full text-center border-0 bg-primary">
                    <CardHeader className="flex justify-center items-center pb-2 text-foreground">
                      <div className="p-2 mb-2 rounded-full border w-fit border-foreground">
                        <RocketIcon />
                      </div>
                      {t('logic.current.step.label', {
                        stepName: lastReachedStep?.name ?? `${t('program.step.name')} 0`
                      })}
                    </CardHeader>

                    <CardContent className="pb-4 text-2xl font-bold text-foreground">
                      {pointsTranslation(t, balance, true)}
                    </CardContent>

                    {lastReachedStep && lastReachedStep.description != '' && (
                      <CardFooter className="text-foreground">
                        <div className="m-auto w-6/12">{lastReachedStep?.description}</div>
                      </CardFooter>
                    )}
                  </Card>

                  <Card
                    className={`w-full border-0 flex-col justify-center text-center ${
                      isLastStepReached ? 'hidden' : ''
                    }`}
                  >
                    <CardHeader className="flex justify-center items-center pb-2 text-foreground">
                      <div className="p-2 mb-2 rounded-full border w-fit border-foreground">
                        <RocketIcon />
                      </div>

                      {t('logic.next.step.label', {
                        nextStepName: nextStep?.name
                      })}
                    </CardHeader>

                    <CardContent className="pb-4 text-2xl font-bold text-foreground">
                      {pointsTranslation(t, nextStep?.stepPoint, true)}
                    </CardContent>

                    {nextStep && nextStep.description != '' && (
                      <CardFooter className="text-foreground">
                        <div className="m-auto w-6/12">{nextStep?.description}</div>
                      </CardFooter>
                    )}
                  </Card>
                </div>
              </div>
            )}

            {isLinearLogicWorkspace(workspace) && (
              <div className="flex flex-wrap space-y-4 sm:flex-nowrap sm:space-x-4 sm:space-y-0">
                <Card className="flex flex-col gap-3 justify-between py-12 w-full text-xl text-center border-0 bg-primary">
                  <CardHeader className="flex justify-center items-center py-0 text-primary-foreground">
                    <div className="p-3 mb-3 rounded-full border w-fit border-primary-foreground">
                      <RocketIcon className="size-7" />
                    </div>
                    {t('home.linear.kpi.label.points')}
                  </CardHeader>

                  <CardContent className="py-0 text-5xl font-bold text-primary-foreground">
                    <CountUp
                      className="font-bold"
                      end={balance}
                      duration={2}
                      formattingFn={(n) => pointsTranslation(t, n, true)}
                    />
                  </CardContent>

                  {pointExpirationDate && (
                    <CardFooter className="py-0 text-primary-foreground">
                      <div className="m-auto">
                        {t('home.linear.kpi.footer', { expirationDate: pointExpirationDate })}
                      </div>
                    </CardFooter>
                  )}
                </Card>
              </div>
            )}

            {isTargetLogicWorkspace(workspace) && (
              <div className="space-y-4 w-full xl:min-w-6/12">
                {isTargetStatusVisible() && (
                  <Card className="hidden p-7 text-center border-0 xs:block">
                    <ProgressTarget
                      targetStatus={targetStatus}
                      currencySymbol={currencySymbol}
                      isTargetPointLogic={isTargetPointLogic}
                    />
                  </Card>
                )}

                <div className="flex flex-wrap space-y-4 sm:flex-nowrap sm:space-x-4 sm:space-y-0">
                  {isTargetStatusVisible() && (
                    <Card
                      className={cn(
                        'flex flex-col justify-center w-full text-center border-0 bg-primary',
                        !isTargetStatusVisible() ? 'w-full' : 'sm:w-6/12'
                      )}
                    >
                      <CardHeader className="flex justify-center items-center pb-2 text-primary-foreground">
                        <div className="p-2 mb-2 rounded-full border w-fit border-primary-foreground">
                          <RocketIcon />
                        </div>
                        {t('home.target.kpi.label.level')}
                      </CardHeader>

                      <CardContent className="pb-4 text-2xl font-bold text-primary-foreground">
                        <CountUp
                          className="w-full text-2xl font-bold lg:w-6/12"
                          end={targetStatus?.value}
                          duration={2}
                          formattingFn={(n) =>
                            String(FormattedNumber({ value: n, style: 'decimal' }))
                          }
                          suffix={
                            isTargetPointLogic
                              ? ` ${pointsTranslation(t, targetStatus?.value ?? 0, false)}`
                              : ' €'
                          }
                        />
                      </CardContent>

                      <CardFooter className="text-primary-foreground">
                        <div className="m-auto">{t('home.target.kpi.footer.level')}</div>
                      </CardFooter>
                    </Card>
                  )}
                  <Card
                    className={cn(
                      'flex flex-col justify-center w-full text-center border-0 bg-foreground',
                      !isTargetStatusVisible() ? 'w-full' : 'sm:w-6/12'
                    )}
                  >
                    <CardHeader className="flex justify-center items-center pb-2 text-primary-foreground">
                      <div className="p-2 mb-2 rounded-full border w-fit border-primary-foreground">
                        <CoinsIcon />
                      </div>
                      {t('home.target.kpi.label.nextlevel')}
                    </CardHeader>

                    <CardContent className="pb-4 text-2xl font-bold text-primary-foreground">
                      <CountUp
                        className="font-bold"
                        end={balance}
                        duration={2}
                        formattingFn={(n) =>
                          String(FormattedNumber({ value: n, style: 'decimal' }))
                        }
                        suffix={` ${pointsTranslation(t, balance)}`}
                      />
                    </CardContent>

                    <CardFooter className="text-primary-foreground">
                      <div className="m-auto">{t('home.target.kpi.footer.nextlevel')}</div>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            )}

            {isStepLogicWorkspace(workspace) && workspaceSteps && (
              <Card className="flex overflow-hidden relative flex-col justify-between w-full border-0 grow">
                <HomeSlider programSlides={programSlides} defaultSlide={defaultSlide} />
              </Card>
            )}

            {isLinearLogicWorkspace(workspace) && (
              <Card
                className={cn(
                  'flex overflow-hidden relative flex-col justify-between w-full border-0 grow xl:col-span-2'
                )}
              >
                <HomeSlider programSlides={programSlides} defaultSlide={defaultSlide} />
              </Card>
            )}

            {isTargetLogicWorkspace(workspace) && (
              <Card
                className={cn(
                  'relative flex w-full grow flex-col justify-between overflow-hidden border-0',
                  !isTargetStatusVisible() && 'xl:col-span-2'
                )}
              >
                <HomeSlider programSlides={programSlides} defaultSlide={defaultSlide} />
              </Card>
            )}
          </div>
        )}

        {activeHomeWidgetWorkspaces.length > 0 && (
          <div className="pt-7">
            <WidgetHome widgetWorkspaces={activeHomeWidgetWorkspaces} />
          </div>
        )}
      </section>

      <Marquee text={workspaceConfigContext?.groupedConfigs?.core?.title ?? null} />
    </PageContainer>
  );
};

export default HomePage;
